import { toDocument, type Document } from '@/store/parser'
import type { AfpDocument } from 'afpnews-api'
import { columns } from './state'
import {
  AGENDA_GENRE_IDS,
  DEFAULT_EXCLUDED_GENRE_IDS,
  excludeAgenda,
  excludeGenreIds,
  getColumnById
} from './getters'
import { isLoggedIn, supabase } from '@/plugins/supabase'
import { forceRefreshAfpToken } from '@/plugins/api'
import type { PostgrestError } from '@supabase/supabase-js'
import Dexie from 'dexie'
import { type SearchQueryParams, defaultSearchParams } from 'afpnews-api'
import type { Column } from '@/types'
import db from '@/plugins/database'
import { moveArrayItem } from '@/lib/utils'
import { dequal } from 'dequal'
import { track } from '@vercel/analytics'
import { useNotify } from '@/composables/useNotify'
import { i18n } from '@/plugins/i18n'
import { MAX_COLUMNS } from '@/config/deck/constants'
import { eventIdsFromQuery, isEventExpired, mergeEvents } from '@/lib/events'

function dbError(action: string, e: unknown): never {
  const msg = e instanceof Error ? e.message : String(e)
  console.error(`[mutations] ${action} failed`, e)
  useNotify().error(msg)
  throw e
}

// Opérations idempotentes : on log l'échec sans le remonter à l'UI.
function dbErrorSwallow(action: string, e: unknown): void {
  console.warn(`[mutations] ${action} failed (idempotent, ignored)`, e)
}

// Le JWT Supabase peut expirer (autoRefreshToken désactivé) alors que le token AFP reste valide : RLS 401.
function isSupabaseAuthError({
  error,
  status
}: {
  error: PostgrestError | null
  status: number
}): boolean {
  if (!error) return false
  return (
    status === 401 ||
    error.code === 'PGRST301' ||
    /jwt|token|expired|unauthor/i.test(error.message)
  )
}

// Sur un refus d'auth, force un refresh des tokens et retente une fois ; notifie si ça échoue encore.
async function persistColumns(
  action: string,
  run: () => PromiseLike<{ error: PostgrestError | null; status: number }>
) {
  let res = await run()
  if (isSupabaseAuthError(res)) {
    try {
      await forceRefreshAfpToken()
    } catch {
      // refresh KO : la notification ci-dessous informera l'utilisateur
    }
    res = await run()
  }
  if (res.error) {
    console.error(`[mutations] ${action} (supabase) failed`, res.error)
    useNotify().error(res.error.message)
  }
}

function generateDefaultColumn(order: number = 0): Column {
  return {
    id: crypto.randomUUID(),
    type: 'search',
    lastUpdated: Date.now(),
    params: Object.assign({}, defaultSearchParams as SearchQueryParams, {
      provider: ['afp', 'أ ف ب'],
      size: 10,
      // "all" exclut les photos par défaut ; le fil photo se récupère en sélectionnant la classe 'picture'.
      class: { exclude: ['picture'] },
      genreid: {
        exclude: [...DEFAULT_EXCLUDED_GENRE_IDS]
      }
    }),
    documentsIds: [],
    order
  }
}

// L'ordre du tableau est respecté à l'affichage : le premier élément reçoit l'order le plus bas (tête de deck).
export async function addColumns(payloads: Partial<Column>[]) {
  if (payloads.length === 0) return

  const currentCount = columns.value?.length ?? 0
  const available = MAX_COLUMNS - currentCount
  if (available <= 0) {
    useNotify().error(
      i18n.global.t('column.limit-reached', { max: MAX_COLUMNS })
    )
    return
  }
  if (payloads.length > available) {
    useNotify().error(
      i18n.global.t('column.limit-reached', { max: MAX_COLUMNS })
    )
    payloads = payloads.slice(0, available)
  }

  const orders = columns.value.map(d => d.order)
  const baseOrder = orders.length ? Math.min(...orders) : 0
  const startOrder = baseOrder - payloads.length

  const newColumns: Column[] = payloads.map((payload, i) => {
    const defaultColumn = generateDefaultColumn(startOrder + i)
    if (payload.params) {
      payload.params = Object.assign(defaultColumn.params, payload.params)
    }
    return Object.assign(defaultColumn, payload)
  })

  try {
    await db.columns.bulkPut(newColumns)
  } catch (e) {
    dbError('addColumns', e)
  }
  if (isLoggedIn.value) {
    await persistColumns('addColumns', () =>
      supabase.from('columns').insert(
        newColumns.map(col => ({
          id: col.id,
          type: col.type,
          params: col.params,
          order: col.order,
          highlight: col.highlight ?? true
        }))
      )
    )
  }
}

export function addColumn(payload?: Partial<Column>) {
  return addColumns([payload ?? {}])
}

// On ne vide documentsIds (cycle de refresh local) que si la recherche a changé, pas sur order/highlight seuls.
function remoteColumnChanges(
  current: Column,
  incoming: Column,
  defaultParams: SearchQueryParams
): Partial<Column> {
  const params = Object.assign(
    {},
    defaultParams,
    current.params,
    incoming.params
  )
  const changes: Partial<Column> = {
    type: incoming.type ?? current.type,
    order: incoming.order ?? current.order,
    highlight: incoming.highlight ?? current.highlight ?? true,
    params
  }
  if (!dequal(params, current.params)) changes.documentsIds = []
  return changes
}

// N'écrit que si les changements ont un effet, pour éviter une émission liveQuery (re-render) inutile.
async function updateColumnIfChanged(
  current: Column,
  changes: Partial<Column>
) {
  if (dequal({ ...current, ...changes }, current)) return
  await db.columns.update(current.id, changes)
}

// Transaction unique, lecture comprise : une seule émission liveQuery, sans écraser un refresh concurrent.
export async function syncColumnsFromSupabase(incomingColumns: Column[]) {
  const defaultParams = generateDefaultColumn().params
  const incomingById = new Map(incomingColumns.map(c => [c.id, c]))

  await db.transaction('rw', db.columns, async () => {
    const currentColumns = await db.columns.toArray()
    const currentById = new Map(currentColumns.map(c => [c.id, c]))

    const toDelete = currentColumns
      .filter(col => !incomingById.has(col.id))
      .map(col => col.id)
    if (toDelete.length) await db.columns.bulkDelete(toDelete)

    // Écritures indépendantes (une clé par colonne) : sûr de paralléliser dans
    // la même transaction Dexie (cf. doc Dexie sur Promise.all en transaction).
    await Promise.all(
      currentColumns
        .filter(col => incomingById.has(col.id))
        .map(col => {
          const incoming = incomingById.get(col.id)!
          return updateColumnIfChanged(
            col,
            remoteColumnChanges(col, incoming, defaultParams)
          )
        })
    )

    const toInsert = incomingColumns
      .filter(col => !currentById.has(col.id))
      .map(col =>
        Object.assign({}, col, {
          highlight: col.highlight ?? true,
          params: Object.assign({}, defaultParams, col.params),
          documentsIds: [],
          lastUpdated: Date.now()
        })
      )
    if (toInsert.length) await db.columns.bulkPut(toInsert)
  })
}

// Événements realtime : colonne créée ou modifiée sur un autre appareil
export async function applyRemoteColumnInsert(incoming: Column) {
  const existing = await db.columns.get(incoming.id)
  if (existing) return
  await db.columns.put({
    ...incoming,
    highlight: incoming.highlight ?? true,
    params: Object.assign({}, generateDefaultColumn().params, incoming.params),
    documentsIds: [],
    lastUpdated: Date.now()
  })
}

export function applyRemoteColumnUpdate(incoming: Column) {
  const defaultParams = generateDefaultColumn().params
  return db.transaction('rw', db.columns, async () => {
    const current = await db.columns.get(incoming.id)
    if (!current) return
    await updateColumnIfChanged(
      current,
      remoteColumnChanges(current, incoming, defaultParams)
    )
  })
}

export async function moveColumn(
  columnId: string,
  dir: 'left' | 'right' | number
) {
  const sortingArray = columns.value.map(d => d.id)
  const indexCol = sortingArray.findIndex(d => d === columnId)
  const newArray = moveArrayItem(sortingArray, indexCol, dir)

  await db.transaction('rw', db.columns, () => {
    return Promise.all(
      newArray.map((id, index) => db.columns.update(id, { order: index }))
    )
  })

  if (isLoggedIn.value) {
    await persistColumns('moveColumn', () =>
      supabase.rpc('reorder_columns', {
        column_orders: newArray.map((id, index) => ({ id, order: index }))
      })
    )
  }
}

export async function closeColumn(columnId: string) {
  try {
    await db.columns.delete(columnId)
  } catch (e) {
    dbErrorSwallow('closeColumn', e)
  }
  if (isLoggedIn.value) {
    await persistColumns('closeColumn', () =>
      supabase.from('columns').delete().eq('id', columnId)
    )
  }
}

export async function resetColumn(columnId: string) {
  try {
    await db.columns.where('id').equals(columnId).modify({ documentsIds: [] })
  } catch (e) {
    dbErrorSwallow('resetColumn', e)
  }
}

export async function toggleAgenda(columnId: string) {
  const column = getColumnById(columnId)
  if (!column) return
  const newExcludeGenreIds = excludeAgenda(columnId)
    ? excludeGenreIds(columnId).filter(
        d => !AGENDA_GENRE_IDS.find(e => e === d)
      )
    : [...excludeGenreIds(columnId), ...AGENDA_GENRE_IDS]
  await updateColumnParams(columnId, {
    ...column.params,
    genreid: {
      exclude: newExcludeGenreIds
    }
  })
  await resetColumn(columnId)
}

export async function updateColumnParams(
  columnId: string,
  params: SearchQueryParams
) {
  track('update_column_params', {
    langs: params.langs?.join('|') || '',
    query: params.query || ''
  })
  await db.columns
    .where('id')
    .equals(columnId)
    .modify({ params: Dexie.deepClone(params) })

  if (isLoggedIn.value) {
    await persistColumns('updateColumnParams', () =>
      supabase.from('columns').update({ params }).eq('id', columnId)
    )
  }
}

// toDocument() peut lever pour des raisons propres à l'UI (ex. aucune rendition affichable sur
// un document photo) même sur un AfpDocument valide — indépendant de la tolérance côté SDK
// (apicore.search/searchAll avec { parse: true, lenient: true }, qui filtre déjà les documents
// bruts invalides en amont).
export function toDocuments(afpDocs: AfpDocument[]): Document[] {
  return afpDocs.flatMap(afpDoc => {
    try {
      return [toDocument(afpDoc)]
    } catch (e) {
      console.debug(`Erreur lors de la conversion du document`, afpDoc)
      console.warn(e)
      return []
    }
  })
}

export async function addDocuments(afpDocs: AfpDocument[]) {
  if (Array.isArray(afpDocs)) {
    const docs = toDocuments(afpDocs)
    try {
      await db.transaction('rw', [db.documents, db.events], async () => {
        await db.documents.bulkPut(Dexie.deepClone(docs))
        const ids = [
          ...new Set(docs.flatMap(d => (d.events ?? []).map(e => e.id)))
        ]
        if (!ids.length) return
        const existing = await db.events.bulkGet(ids)
        await db.events.bulkPut(mergeEvents(existing, docs))
      })
    } catch (e) {
      dbError('addDocuments', e)
    }
    return docs
  }
  return []
}

// Read-modify-write atomique (relecture dans la transaction) pour que deux refresh concurrents ne s'écrasent pas.
// replaceExisting : le batch ne couvre pas tout l'intervalle, fusionner laisserait un trou ⇒ on repart du batch.
export function prependDocumentsIdsToCol(
  columnId: string,
  documentsIds: string[],
  replaceExisting = false
) {
  return db.transaction('rw', db.columns, async () => {
    const column = await db.columns.get(columnId)
    if (!column) return
    const existingDocumentsIds = replaceExisting ? [] : column.documentsIds
    const merged = [...new Set([...documentsIds, ...existingDocumentsIds])]
    if (dequal(merged, column.documentsIds)) return
    await db.columns.update(columnId, {
      documentsIds: merged,
      lastUpdated: Date.now()
    })
  })
}

export function appendDocumentsIdsToCol(
  columnId: string,
  documentsIds: string[]
) {
  return db.transaction('rw', db.columns, async () => {
    const column = await db.columns.get(columnId)
    if (!column) return
    const merged = [...new Set([...column.documentsIds, ...documentsIds])]
    if (dequal(merged, column.documentsIds)) return
    await db.columns.update(columnId, {
      documentsIds: merged,
      lastUpdated: Date.now()
    })
  })
}

export async function cleanColumns() {
  try {
    // Transaction sur toutes les tables : un refresh concurrent ne peut pas écrire entre lecture et suppression.
    await db.transaction(
      'rw',
      [db.columns, db.documents, db.events],
      async () => {
        const allColumns = await db.columns.toArray()
        const displayedIds = new Set(allColumns.flatMap(c => c.documentsIds))
        const allStoredDocumentsIds = await db.documents
          .toCollection()
          .primaryKeys()
        const documentsIdsToDelete = allStoredDocumentsIds.filter(
          id => !displayedIds.has(id)
        )
        if (documentsIdsToDelete.length) {
          await db.documents.bulkDelete(documentsIdsToDelete)
          console.debug(
            `${documentsIdsToDelete.length} document(s) supprimé(s) de la base de données locale`
          )
        }
        const referencedEventIds = new Set(
          allColumns.flatMap(c => eventIdsFromQuery(c.params.query))
        )
        const expiredEventIds = (await db.events.toArray())
          .filter(ev => isEventExpired(ev, referencedEventIds))
          .map(ev => ev.id)
        if (expiredEventIds.length) await db.events.bulkDelete(expiredEventIds)
      }
    )
  } catch (e) {
    dbErrorSwallow('cleanColumns', e)
  }
}

export async function clearStorage() {
  await db.documents.clear()
  await db.columns.clear()
  await db.events.clear()
}
